import React, { useState } from "react";
import { useProjectContext } from "../layout/ProjectContext";
import {
  useGetDeviceLogsQuery,
  type DeviceOperationLog,
} from "../features/device-logs/apiSlice";
import EmptyHint from "../components/EmptyHint";

function opBadge(op: string): React.ReactElement {
  if (op === "CREATE") return <span className="badge ok">Create</span>;
  if (op === "DELETE") return <span className="badge alarm">Delete</span>;
  return <span className="badge warn">Update</span>;
}

function summariseChange(log: DeviceOperationLog): string {
  const cf = log.changed_fields;
  if (!cf) return "—";
  try {
    const obj = typeof cf === "string" ? JSON.parse(cf) : cf;
    const keys = Array.isArray(obj) ? obj : Object.keys(obj);
    return keys.length ? keys.join(", ") : "—";
  } catch {
    return String(cf);
  }
}

const DeviceLogs: React.FC = () => {
  const { projectId } = useProjectContext();

  const [operationType, setOperationType] = useState<string>("");
  const [operatorName, setOperatorName] = useState<string>("");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { data, isFetching } = useGetDeviceLogsQuery(
    {
      projectId: projectId ?? undefined,
      operationType: operationType || undefined,
      operatorName: operatorName || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      limit: 200,
    },
    { skip: projectId == null, refetchOnMountOrArgChange: true },
  );

  const logs = data?.logs ?? [];

  if (projectId == null) {
    return (
      <EmptyHint
        icon="bi-folder2-open"
        title="Select a project"
        subtitle="Choose a project from the top bar to view the operation log."
      />
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Checkpoint Alteration Log </h1>
          <div className="sub">
            {data?.total ?? 0} record{(data?.total ?? 0) === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Filters</h2>
        </div>
        <div className="card-body">
          <div className="grid cols-4">
            <div className="field">
              <label>Operation</label>
              <select
                className="select"
                value={operationType}
                onChange={(e) => setOperationType(e.target.value)}
              >
                <option value="">All</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
              </select>
            </div>
            <div className="field">
              <label>Operator</label>
              <input
                className="input"
                value={operatorName}
                onChange={(e) => setOperatorName(e.target.value)}
                placeholder="Name contains…"
              />
            </div>
            <div className="field">
              <label>From</label>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="field">
              <label>To</label>
              <input
                className="input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head">
          <h2>History</h2>
        </div>
        {isFetching ? (
          <EmptyHint
            icon="bi-hourglass-split"
            title="Loading…"
            subtitle="Fetching operation logs."
          />
        ) : logs.length === 0 ? (
          <EmptyHint
            icon="bi-clock-history"
            title="No operations"
            subtitle="No checkpoint operations match the current filters."
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>When</th>
                <th>Checkpoint</th>
                <th>Operation</th>
                <th>Changed fields</th>
                <th>Operator</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="text-muted">
                    {new Date(log.operation_timestamp).toLocaleString()}
                  </td>
                  <td className="fw-600">
                    {log.device_name ?? log.device_id}
                    {log.device_type && (
                      <div className="text-sm text-muted">
                        {log.device_type}
                      </div>
                    )}
                  </td>
                  <td>{opBadge(log.operation_type)}</td>
                  <td className="text-sm">{summariseChange(log)}</td>
                  <td>{log.operator_name ?? "—"}</td>
                  <td className="text-sm text-muted">
                    {log.operation_reason ?? "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default DeviceLogs;
